import { useEffect, useRef } from 'react';

interface ParsesTo<T> {
  safeParse: (values: unknown) => { success: boolean; data?: T };
}

interface FlushStepOnExitArgs<T> {
  /** Sends ON_SAVE to the step's machine — persists without advancing. */
  save: (values: T) => void;
  /** react-hook-form's `formState.isDirty`. Nothing to flush when false. */
  isDirty: boolean;
  /** react-hook-form's `getValues`. Read lazily, at unmount. */
  getValues: () => unknown;
  /** The step's zod schema, used to reject a half-typed form (see below). */
  schema: ParsesTo<T>;
  /**
   * Reads the wizard's current step index from the parent machine. Must read
   * live (e.g. `actor.getSnapshot().context.currentStep`) rather than close
   * over a rendered value — see the note on redundant writes below.
   */
  getCurrentStep: () => number;
}

/**
 * [MCV-1543] Persist a wizard step's in-progress work as it leaves the screen.
 *
 * The listing wizard renders one step at a time, so moving between steps
 * unmounts the active form and takes react-hook-form's state with it. Only the
 * forward path wrote anything — "Previous" just sends ON_PREV — so anything
 * typed on Shift Details or Additional Terms and not followed by "Next" was
 * silently discarded, and returning to the step showed empty fields.
 *
 * Two things this deliberately does not do:
 *
 * - It does not send a half-typed form. Values are validated first, because a
 *   partial form would otherwise PATCH empty strings into numeric columns. The
 *   user sees those fields fail validation on the way forward regardless, so
 *   dropping the flush is better than persisting garbage.
 * - It does not repeat a write that already happened. A step that advanced got
 *   there through ON_UPDATE, which persists the same values, so flushing again
 *   would fire a redundant second PATCH.
 *
 * That second point is why the step index is compared instead of latching a
 * boolean when the form is submitted. Submitting is not the same as having
 * saved: the step machine's error path returns to $_VIEW *without* advancing,
 * leaving the user on the step with edits that were never written. A latch set
 * at submit time would suppress the flush for the rest of that mount, so a
 * failed save would silently disarm the very safety net this hook exists to
 * provide, and the next "Previous" would discard the user's work.
 *
 * Only a step that actually advanced has been written, and advancing is
 * observable: the wizard's `currentStep` moved past this step. It is read from
 * the machine at cleanup time rather than from a ref updated during render,
 * because changing `currentStep` swaps in a different step component — this
 * form unmounts without ever rendering again, so a render-time snapshot would
 * still hold the old index.
 */
export const useFlushStepOnExit = <T>({
  save,
  isDirty,
  getValues,
  schema,
  getCurrentStep,
}: FlushStepOnExitArgs<T>) => {
  const stepOnMountRef = useRef<number | null>(null);
  if (stepOnMountRef.current === null) {
    stepOnMountRef.current = getCurrentStep();
  }

  // Held in a ref and reassigned each render so the unmount cleanup — which
  // runs with the closure from mount — still sees the latest values.
  const flushRef = useRef<() => void>(() => undefined);
  flushRef.current = () => {
    if (!isDirty) return;
    const stepOnMount = stepOnMountRef.current;
    if (stepOnMount !== null && getCurrentStep() > stepOnMount) return;
    const parsed = schema.safeParse(getValues());
    if (!parsed.success || parsed.data === undefined) return;
    save(parsed.data);
  };

  useEffect(() => () => flushRef.current(), []);
};
